// src/containers/oura-processing-toast/index.tsx
//
// The Oura equivalent of AiqmProcessingToast / KbProcessingToast: a bottom-right
// card reporting long-running background work, so the user can navigate away
// and still see progress. Same shape as those two on purpose — header (spinner
// or check + label + collapse + dismiss), a sub-row with the terminal CTA, then
// per-item rows with retry/remove on failures.
//
// Differences from the other two, both deliberate:
//   • It reads the EXISTING scan-job store (`@oura/aeo`) rather than adding a
//     third upload context. That store already owns create + poll + terminal
//     state, so a parallel context would be duplicate state to keep in sync.
//   • An Oura "item" is a 5–15 minute server-side assessment, not a file
//     upload, so rows show the pipeline stage ("Step 6 of 13 · Probing AI
//     engines") instead of an upload percentage.

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    Loader2Icon,
    XIcon,
    CheckCircle2Icon,
    AlertCircleIcon,
    ChevronUpIcon,
    ChevronDownIcon,
    RefreshCwIcon,
    Building2Icon,
    ClockIcon,
    BanIcon,
} from "lucide-react";
import { Card } from "@component/card";
import { Button } from "@component/button";
import {
    useScanJobs,
    removeScanJob,
    clearSettledScanJobs,
    retryScanJob,
    cancelScanJob,
    rehydrateScanJobs,
    type ScanJob,
} from "@oura/aeo";
import { OURA_ROOT } from "@oura/nav";
import { useOuraRole } from "@oura/role";
import { useUserProfileContext } from "@context/user_profile";
import { isSessionlessRoute } from "@util/session";
import { cn } from "@util/tailwind";
import { useWritesPaused } from "@context/maintenance";
import { stageLabel } from "@oura/stageLabels";
import { StageChecklist, EtaLine } from "@oura/components/aeo/StageChecklist";


/** Short, human reason a run ended badly — the toast has far less room than the table. */
const FAILURE_NOTE: Record<string, string> = {
    ENGINE_STUBBED: "Engine not provisioned yet",
    SITE_UNREACHABLE: "Could not reach the site",
    SCRAPE_TIMEOUT: "Could not reach the site",
    ENGINE_NOT_INSTALLED: "Scoring engine not deployed",
    MISSING_GOOSE_API_KEY: "Missing API key — tell engineering",
    GOOSE_API_KEY_FAILED: "API key rejected — tell engineering",
    GEMINI_THROTTLE_TIMEOUT: "Hit today's AI quota",
    ENGINE_TIMEOUT: "Took too long and was stopped",
    ENGINE_FAILED: "Scoring engine crashed",
    ENGINE_BAD_OUTPUT: "Unreadable engine output",
    STAGE_CRASH: "Failed partway through",
    POLL_TIMEOUT: "Still running after 30 minutes",
    POLL_FAILED: "Lost track of this run",
};


const isTerminalJob = (j: ScanJob) =>
    j.status === "complete" || j.status === "partial" || j.status === "failed"
    || j.status === "untracked" || j.status === "cancelled";

function JobLine({ job }: { job: ScanJob }) {
    const [cancelling, setCancelling] = useState(false);
    const writesPaused = useWritesPaused();
    const failed = job.status === "failed";
    // We stopped watching after 30 minutes; the run may still be going on the
    // server. Rendered as a neutral notice with a way to look again — NOT as a
    // failure, which is what it used to claim and which is not something this
    // browser is in any position to know.
    const untracked = job.status === "untracked";
    // Someone stopped this on purpose. Neutral grey, no alarm icon, no retry
    // prompt — the operator knows exactly what happened, they did it.
    const cancelled = job.status === "cancelled";
    // `partial` is a real, usable result (degraded), not a failure — the table
    // labels it "Preliminary" and so should this.
    const done = job.status === "complete" || job.status === "partial";
    const running = job.status === "queued" || job.status === "running";
    const hasSteps = job.stages_total > 0;
    const stepNum = hasSteps ? Math.min(job.stages_complete + 1, job.stages_total) : 0;
    const label = stageLabel(job.stage);
    const pct = hasSteps ? Math.round((job.stages_complete / job.stages_total) * 100) : 0;
    // The checklist needs the backend's ordered array; without it there is no
    // honest way to say WHICH steps are done, only how many.
    const stages = job.stages ?? [];
    const doneCount = stages.filter((st) => st.state === "done").length;
    const canExpand = stages.length > 0;
    const [showStages, setShowStages] = useState(false);

    return (
        <div className="flex items-start gap-3 px-4 py-2.5">
            {/* No per-row spinner while running: the header already shows one,
                and repeating it on every row is noise. Terminal icons stay —
                those carry per-item outcome the header can't. */}
            {(failed || done || untracked || cancelled) && (
                <div className="mt-0.5 flex-shrink-0">
                    {failed ? (
                        <AlertCircleIcon className="h-4 w-4 text-rose-500" />
                    ) : untracked ? (
                        <ClockIcon className="h-4 w-4 text-amber-500" />
                    ) : cancelled ? (
                        <BanIcon className="h-4 w-4 text-gray-400" />
                    ) : (
                        <CheckCircle2Icon className="h-4 w-4 text-emerald-500" />
                    )}
                </div>
            )}
            <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-foreground" title={job.domain}>
                    {job.business_name || job.domain}
                </p>
                {failed ? (
                    <p className="mt-0.5 truncate text-xs text-rose-600">
                        {FAILURE_NOTE[job.error_code ?? ""] ?? "Assessment failed"}
                    </p>
                ) : untracked ? (
                    <>
                        <p className="mt-0.5 text-xs text-amber-700">
                            Still running after 30 minutes. We stopped checking — it has not failed.
                            Reload to pick it up again.
                        </p>
                        {/* Everything we knew about this run used to vanish at
                            the timeout, which is the moment a user is most
                            anxious about it. The work that finished did finish;
                            we simply stopped watching. */}
                        {hasSteps && (
                            <p className="mt-0.5 truncate text-xs text-muted-foreground">
                                Last seen at step {stepNum} of {job.stages_total}: {label}
                            </p>
                        )}
                        {canExpand && (
                            <StageChecklist stages={stages} className="mt-1.5" />
                        )}
                    </>
                ) : cancelled ? (
                    <p className="mt-0.5 text-xs text-muted-foreground">
                        Cancelled. This domain is free to scan again.
                    </p>
                ) : done ? (
                    <p className="mt-0.5 truncate text-xs text-muted-foreground">
                        {job.status === "partial"
                            ? FAILURE_NOTE[job.error_code ?? ""] ?? "Finished, some numbers incomplete"
                            : "Scan complete"}
                    </p>
                ) : (
                    <>
                        <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-muted">
                            <div
                                className="h-full rounded-full bg-blue-600 transition-all"
                                style={{ width: `${pct}%` }}
                            />
                        </div>
                        <div className="mt-1 flex items-center justify-between gap-2">
                            <p className="my-0 truncate text-xs text-muted-foreground">
                                {hasSteps
                                    ? `Step ${stepNum} of ${job.stages_total}: ${label}`
                                    : `${label}…`}
                            </p>
                            <EtaLine eta={job.eta} className="flex-shrink-0" />
                        </div>
                        {/* Collapsed by default: several scans can run at once,
                            and sixteen stages unfolded on every row would bury
                            the toast. The disclosure only appears when the
                            backend actually sent the list — a button that opens
                            an empty box is worse than no button. */}
                        {canExpand && (
                            <>
                                {/* Muted, not a blue link: this reveals detail
                                    about the row it sits in, and blue reads as
                                    an action competing with Cancel. */}
                                <button
                                    type="button"
                                    onClick={() => setShowStages((v) => !v)}
                                    aria-expanded={showStages}
                                    className="mt-1.5 inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
                                >
                                    {showStages ? "Hide steps" : `${doneCount} of ${stages.length} steps done`}
                                    <ChevronDownIcon
                                        className={cn("h-3 w-3 transition-transform", showStages && "rotate-180")}
                                    />
                                </button>
                                {/* Scrolls rather than growing: sixteen stages
                                    would otherwise push the toast off-screen. */}
                                {/* No scroller of its own. The job list above
                                    already has one, and a second nested inside
                                    it put two scrollbars side by side and
                                    clipped the list mid-row. One scroll area,
                                    and it is the outer one. */}
                                {showStages && <StageChecklist stages={stages} className="mt-2" />}
                            </>
                        )}
                    </>
                )}
            </div>
            {/* Cancel is the only action on a live row. It stops the remaining
                spend and — the part that actually matters day to day — releases
                the domain immediately, so a scan started against the wrong
                domain or wrong anchors can be replaced straight away instead of
                waiting out the mistake (ourteam-backend#3982). */}
            {running && (
                <button
                    onClick={() => {
                        setCancelling(true);
                        void cancelScanJob(job.assessment_id).finally(() => setCancelling(false));
                    }}
                    disabled={cancelling || writesPaused}
                    title="Stop this scan"
                    className="flex-shrink-0 rounded px-1.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-destructive disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {cancelling ? "Stopping…" : "Cancel"}
                </button>
            )}
            {(failed || cancelled) && (
                <div className="flex flex-shrink-0 items-center gap-0.5">
                    {/* Retry needs the original request, which a job rehydrated
                        after a reload doesn't carry — hide it rather than fire an
                        incomplete assessment.

                        Deliberately NOT offered on a cancelled run: re-submitting
                        the identical request is almost never the intent. You
                        cancel because the domain or the anchors were wrong, and
                        fixing those means New Scan, not a replay. */}
                    {failed && job.input && (
                        <button
                            disabled={writesPaused}
                            onClick={() => void retryScanJob(job.assessment_id)}
                            title="Retry scan"
                            className="rounded p-1 text-blue-500 transition-colors hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <RefreshCwIcon className="h-3.5 w-3.5" />
                        </button>
                    )}
                    <button
                        onClick={() => removeScanJob(job.assessment_id)}
                        title="Remove"
                        className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-destructive"
                    >
                        <XIcon className="h-3.5 w-3.5" />
                    </button>
                </div>
            )}
        </div>
    );
}

export default function OuraProcessingToast() {
    const navigate = useNavigate();
    const location = useLocation();
    const jobs = useScanJobs();
    // Tucked away by default: the card sits over page content (pagination,
    // Refresh, row actions) and was hard to work around. One click opens it.
    const [isExpanded, setIsExpanded] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    // `/oura/report/:id` starts with the Oura root but is the PUBLIC render
    // route (#3498): headless Chromium prints it with no session, so this
    // poller could only ever 401 there. Excluded at the source rather than
    // relying on the 401 being harmless.
    // A staff tool: a merchant has no scans to watch, and rehydrating them read
    // every assessment (2.7 MB, ~6 s on staging) on each of their page loads
    // and floated "Scanning business domain" over their Visibility (#4131).
    // Only once the profile has loaded: before it, useOuraRole() falls back to
    // the dev cookie, which defaults to staff, and the rehydrate below fired once
    // on every merchant page load anyway (staging, 13 Oct, #4131).
    const [profileLoading, profile] = useUserProfileContext();
    const role = useOuraRole();
    const isStaff = !profileLoading && Boolean(profile) && role === "oura-staff";
    const onOura = isStaff && location.pathname.startsWith(OURA_ROOT) && !isSessionlessRoute(location.pathname);

    // Recover scans started before a reload (ourteam-backend#3793 made the
    // domain param optional, which is what makes this possible). Only on the
    // Oura surface — no reason to poll AEO endpoints from Recruiter or Trainer.
    useEffect(() => {
        if (!onOura) return;
        void rehydrateScanJobs();
    }, [onOura]);

    // A new scan starting after a dismiss should bring the toast back.
    const activeCount = jobs.filter((j) => !isTerminalJob(j)).length;
    useEffect(() => {
        if (activeCount > 0) setDismissed(false);
    }, [activeCount]);

    if (!onOura || dismissed || jobs.length === 0) return null;

    const failedCount = jobs.filter((j) => j.status === "failed").length;
    const cancelledCount = jobs.filter((j) => j.status === "cancelled").length;
    const untrackedCount = jobs.filter((j) => j.status === "untracked").length;
    const doneCount = jobs.filter(
        (j) => j.status === "complete" || j.status === "partial",
    ).length;
    const allSettled = activeCount === 0;

    // `untracked` settles this browser's watch, NOT the run. Everything below
    // treats it as the one outcome that must never be summarised as finished.
    const stoppedWatching = untrackedCount > 0 && failedCount === 0;

    const headerLabel = !allSettled
        ? activeCount > 1
            ? `Scanning ${activeCount} business domains`
            : "Scanning business domain"
        : failedCount > 0
            ? "Scans finished (with errors)"
            // "Complete" over a run we merely stopped watching claims the one
            // thing nobody knows. This is the same guard the cancelled case has
            // below, and it binds harder: a cancelled run really did stop,
            // while this one may still be going on the server. Collapsed, the
            // header is all the user sees — and #3698 is the second time this
            // toast has told someone a running scan was over. The first cost a
            // deleted business (see `untracked` in src/oura/aeo.ts).
            //
            // It wins over `doneCount` too, unlike the cancelled guard: three
            // finished scans do not make the fourth one finished, and the
            // sub-row still reads "3 complete · 1 still running".
            : stoppedWatching
                ? untrackedCount > 1 ? "Scans may still be running" : "Scan may still be running"
                : doneCount === 0 && cancelledCount > 0
                    ? cancelledCount > 1 ? "Scans cancelled" : "Scan cancelled"
                    : "Scans complete";

    // Only non-zero outcomes are named, so one cancelled scan reads "1
    // cancelled" rather than "0 complete · 1 cancelled". `untracked` is counted
    // too — it was in neither tally before, so a run we stopped watching
    // reported itself as "0 scans complete".
    const settledParts = [
        doneCount > 0 ? `${doneCount} complete` : null,
        failedCount > 0 ? `${failedCount} failed` : null,
        cancelledCount > 0 ? `${cancelledCount} cancelled` : null,
        untrackedCount > 0 ? `${untrackedCount} still running` : null,
    ].filter(Boolean);

    const subRowText = !allSettled
        ? `${activeCount} ${activeCount === 1 ? "scan" : "scans"} running`
        : settledParts.length > 0
            ? settledParts.join(" · ")
            : `${doneCount} ${doneCount === 1 ? "scan" : "scans"} complete`;

    const dismiss = () => {
        clearSettledScanJobs();
        setDismissed(true);
    };

    return (
        // BELOW THE DRAWER AND DIALOG LAYER, not above it. At z-50 this box sat
        // on top of every drawer that slides in from the right and, at a 1568
        // wide window, covered the Create a doc drawer's Cancel button
        // completely and the edge of Create the doc -- the only way to reach
        // Cancel was to collapse the box first (#4051, #4035). Oura's Drawer is
        // z-40 and its dialogs are z-50, so this sits under both and comes back
        // the moment the drawer closes. A progress box must never be the thing
        // between someone and a button.
        //
        // Collapsed, it is a slim pill flush against the bottom edge so it only
        // covers a sliver of the page; expanded, it floats as the full card.
        <div
            className={cn(
                "fixed right-6 z-30 animate-in slide-in-from-bottom-5 duration-300",
                isExpanded ? "bottom-6" : "bottom-0",
            )}
        >
            <Card
                className={cn(
                    "border-border bg-background shadow-xl",
                    isExpanded ? "w-[420px]" : "w-[300px] rounded-b-none border-b-0",
                )}
            >
                {/* Header */}
                <div
                    className={cn(
                        "flex items-center justify-between bg-muted/30 px-4",
                        isExpanded ? "border-b border-border py-3" : "py-1.5",
                    )}
                >
                    <div className="flex min-w-0 items-center gap-2">
                        {allSettled ? (
                            failedCount > 0 ? (
                                <AlertCircleIcon className="h-5 w-5 flex-shrink-0 text-amber-600" />
                            ) : stoppedWatching ? (
                                // The same amber clock the untracked row carries,
                                // so the header and the row it summarises agree.
                                <ClockIcon className="h-5 w-5 flex-shrink-0 text-amber-500" />
                            ) : doneCount === 0 && cancelledCount > 0 ? (
                                // A green tick over "Scan cancelled" congratulates
                                // the user for stopping something.
                                <BanIcon className="h-5 w-5 flex-shrink-0 text-gray-400" />
                            ) : (
                                <CheckCircle2Icon className="h-5 w-5 flex-shrink-0 text-emerald-600" />
                            )
                        ) : (
                            <Loader2Icon className="h-5 w-5 flex-shrink-0 animate-spin text-blue-600" />
                        )}
                        <span className="truncate text-sm font-semibold text-foreground">
                            {headerLabel}
                        </span>
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-1">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 hover:bg-muted"
                            onClick={() => setIsExpanded((e) => !e)}
                            title={isExpanded ? "Collapse" : "Expand"}
                        >
                            {isExpanded ? (
                                <ChevronDownIcon className="h-4 w-4" />
                            ) : (
                                <ChevronUpIcon className="h-4 w-4" />
                            )}
                        </Button>
                        {/* Dismiss only once nothing is running — closing mid-scan
                            would strand the only progress indicator. */}
                        {allSettled && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 hover:bg-muted"
                                onClick={dismiss}
                                title="Dismiss"
                            >
                                <XIcon className="h-4 w-4" />
                            </Button>
                        )}
                    </div>
                </div>

                {/* Sub-header — folded away while collapsed; the header
                    already says what is happening. */}
                {isExpanded && (
                <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-2">
                    <span className="truncate text-sm text-muted-foreground">{subRowText}</span>
                    {allSettled && doneCount > 0 && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 flex-shrink-0 text-xs text-blue-600 hover:text-blue-700"
                            onClick={() => {
                                navigate(`${OURA_ROOT}/businesses`);
                                dismiss();
                            }}
                        >
                            VIEW DETAILS
                        </Button>
                    )}
                </div>
                )}

                {/* Body */}
                {isExpanded && (
                    <div className="max-h-[26rem] divide-y divide-border overflow-y-auto py-1">
                        {jobs.length === 0 ? (
                            <div className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground">
                                <Building2Icon className="h-4 w-4" />
                                No scans yet
                            </div>
                        ) : (
                            jobs.map((j) => <JobLine key={j.assessment_id} job={j} />)
                        )}
                    </div>
                )}
            </Card>
        </div>
    );
}
